import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createHighlighter } from 'shiki';
import { useDispatch } from 'react-redux';
import { setFocusedTab } from '../../chat/actions.web';
import { ChatTabs } from '../../chat/constants';
import { makeStyles } from 'tss-react/mui';
import { io } from 'socket.io-client';
import { symfonyApi } from '../services/symfonyApi';
import TiptapEditor from '../../chat/components/web/TiptapEditor';
import './OverlayChat.css';
let pendingAuthToken = null;
let shikiHighlighter = null;
// Initialiser Shiki une seule fois
const initShiki = async () => {
    if (!shikiHighlighter) {
        shikiHighlighter = await createHighlighter({
            themes: ['github-dark', 'github-light', 'dark-plus'],
            langs: ['javascript', 'typescript', 'python', 'java', 'php', 'css', 'html', 'json', 'bash', 'sql', 'cpp', 'c', 'go', 'rust', 'swift', 'kotlin', 'scala', 'ruby', 'perl', 'r', 'lua', 'dockerfile', 'yaml', 'xml', 'markdown', 'plaintext']
        });
    }
    return shikiHighlighter;
};
function parseMessagePayload(data) {
    if (data?.postis) {
        return null;
    }
    let payload = data;
    if (typeof payload === "string") {
        try {
            payload = JSON.parse(payload);
        }
        catch {
            return null;
        }
    }
    return payload;
}
// Enum pour les types de messages
var ChatMessageType;
(function (ChatMessageType) {
    ChatMessageType["AUTH_TOKEN"] = "AUTH_TOKEN";
    ChatMessageType["TOGGLE_CHAT_OVERLAY"] = "TOGGLE_CHAT_OVERLAY";
})(ChatMessageType || (ChatMessageType = {}));
var SocketEvent;
(function (SocketEvent) {
    SocketEvent["CONNECT"] = "connect";
    SocketEvent["DISCONNECT"] = "disconnect";
    SocketEvent["CONNECT_ERROR"] = "connect_error";
    SocketEvent["JOIN"] = "join_room";
    SocketEvent["LEAVE"] = "leave_room";
    SocketEvent["MESSAGE"] = "send_message";
    SocketEvent["NEW_MESSAGE"] = "new_message";
    SocketEvent["FILE_MESSAGE"] = "file_message";
    SocketEvent["LOAD_MORE"] = "load_more";
    SocketEvent["HISTORY"] = "room_history";
    SocketEvent["MORE_MESSAGES"] = "more_messages";
    SocketEvent["MARK_AS_READ"] = "mark_as_read";
})(SocketEvent || (SocketEvent = {}));
var ChatContext;
(function (ChatContext) {
    ChatContext["LIVE_SESSION"] = "live-session";
    ChatContext["SUPPORT"] = "SUPPORT";
})(ChatContext || (ChatContext = {}));
var AllowedFileType;
(function (AllowedFileType) {
    AllowedFileType["JPEG"] = "image/jpeg";
    AllowedFileType["PNG"] = "image/png";
    AllowedFileType["GIF"] = "image/gif";
    AllowedFileType["WEBP"] = "image/webp";
    AllowedFileType["PDF"] = "application/pdf";
    AllowedFileType["DOC"] = "application/msword";
    AllowedFileType["DOCX"] = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    AllowedFileType["TXT"] = "text/plain";
})(AllowedFileType || (AllowedFileType = {}));
var FileSizeLimit;
(function (FileSizeLimit) {
    FileSizeLimit[FileSizeLimit["MAX_IMAGE_SIZE"] = 5242880] = "MAX_IMAGE_SIZE";
    FileSizeLimit[FileSizeLimit["MAX_FILE_SIZE"] = 26214400] = "MAX_FILE_SIZE";
})(FileSizeLimit || (FileSizeLimit = {}));
let chatToggleFunction = null;
function showImageOnJitsiOverlay(imageUrl, imageName) {
    if (typeof window !== 'undefined' && imageUrl) {
        window.open(imageUrl, '_blank');
    }
}
const mapServerMessageToClient = (msg, getUserId, updateUserNamesCache, getUserName) => {
    updateUserNamesCache(msg);
    return {
        id: msg._id || msg.id || Date.now().toString(),
        text: msg.text || msg.content || '',
        author: getUserName(msg),
        timestamp: msg.timestamp || msg.createdAt || Date.now(),
        isOwn: Number(msg.senderId || msg.user?.id || msg.senderName) === getUserId(),
        attachment: msg.attachment ? {
            id: msg.attachment.id || Date.now().toString(),
            name: msg.attachment.fileName || msg.attachment.name || 'fichier',
            url: msg.attachment.readUrl || msg.attachment.url || '',
            size: msg.attachment.fileSize || 0,
            type: msg.attachment.fileType || msg.attachment.type || '',
            uploadedAt: msg.attachment.uploadedAt || Date.now(),
            readUrl: msg.attachment.readUrl,
            readUrlExpiresAt: msg.attachment.readUrlExpiresAt,
            gcpPath: msg.attachment.gcpPath || msg.attachment.path || null
        } : undefined
    };
};
const formatName = (first, last) => `${first.charAt(0).toUpperCase() + first.slice(1).toLowerCase()} ${last.toUpperCase()}`;
const getJwtToken = () => localStorage.getItem('CHAT_AUTH_TOKEN');
const getUserIdFromToken = () => {
    const token = getJwtToken();
    if (!token)
        return 1;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return Number(payload.id || payload.userId || payload.sub || payload.username);
    }
    catch {
        return 1;
    }
};
if (typeof window !== "undefined") {
    const earlyListener = (event) => {
        const payload = parseMessagePayload(event.data);
        if (!payload) {
            return;
        }
        if (payload?.type === ChatMessageType.AUTH_TOKEN && payload?.token) {
            pendingAuthToken = payload.token;
            localStorage.setItem('CHAT_AUTH_TOKEN', payload.token);
        }
        if (payload?.type === ChatMessageType.TOGGLE_CHAT_OVERLAY && chatToggleFunction) {
            chatToggleFunction();
        }
    };
    window.addEventListener("message", earlyListener);
}
// Utilitaires de détection d'URL expirées
const isExpired = (url) => {
    if (!url)
        return true;
    try {
        const urlObj = new URL(url);
        const expires = urlObj.searchParams.get('X-Goog-Expires');
        const date = urlObj.searchParams.get('X-Goog-Date');
        if (expires && date) {
            const expiresDuration = parseInt(expires);
            const year = date.substring(0, 4);
            const month = date.substring(4, 6);
            const day = date.substring(6, 8);
            const hour = date.substring(9, 11);
            const minute = date.substring(11, 13);
            const second = date.substring(13, 15);
            const dateMs = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}Z`).getTime();
            const expiresMs = dateMs + (expiresDuration * 1000);
            const currentMs = Date.now();
            return currentMs > expiresMs;
        }
    }
    catch (error) {
    }
    return false;
};
const getAttachmentIdFromUrl = (url) => {
    if (!url)
        return null;
    try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;
        const parts = pathname.split('/');
        return parts[parts.length - 1] || null;
    }
    catch {
        return null;
    }
};
const buildGcpPathFromUrl = (url) => {
    if (!url)
        return null;
    try {
        const urlObj = new URL(url);
        const parts = urlObj.pathname.split('/').filter(Boolean);
        const bucketIndex = parts.findIndex(part => part === 'chat-attachments');
        if (bucketIndex === -1) {
            return null;
        }
        return parts.slice(bucketIndex + 1).join('/');
    }
    catch (error) {
        return null;
    }
};
const useStyles = makeStyles()((theme, { width }) => ({
    chatContainer: {
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(10px)',
        flexShrink: 0,
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        transition: 'width .16s ease-in-out',
        width: `${width}px`,
        height: '100vh',
        zIndex: 300,
        display: 'flex',
        flexDirection: 'column',
        borderRight: `1px solid rgba(255, 255, 255, 0.2)`,
        boxShadow: '2px 0 8px rgba(0, 0, 0, 0.3)',
        '&:hover, &:focus-within': {
            '& .dragHandleContainer': {
                visibility: 'visible'
            }
        },
        '@media (max-width: 580px)': {
            height: '100dvh',
            position: 'fixed',
            left: 0,
            right: 0,
            top: 0,
            width: 'auto'
        },
        '*': {
            userSelect: 'text',
            '-webkit-user-select': 'text'
        }
    },
    chatHeader: {
        height: '60px',
        position: 'relative',
        width: '100%',
        zIndex: 1,
        display: 'flex',
        justifyContent: 'space-between',
        padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
        alignItems: 'center',
        boxSizing: 'border-box',
        color: theme.palette.text01,
        borderBottom: `1px solid ${theme.palette.divider}`,
        ...theme.typography.heading6,
        lineHeight: 'unset',
        fontWeight: theme.typography.heading6.fontWeight,
        '.jitsi-icon': {
            cursor: 'pointer'
        }
    },
    chatTitle: {
        margin: 0,
        fontSize: '16px',
        fontWeight: 'bold',
        color: 'white',
    },
    closeButton: {
        background: 'none',
        border: 'none',
        color: 'white',
        fontSize: '20px',
        cursor: 'pointer',
        padding: '4px 8px',
        borderRadius: '4px',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
    },
    dragHandleContainer: {
        position: 'absolute',
        left: '-3px',
        top: 0,
        bottom: 0,
        width: '6px',
        cursor: 'col-resize',
        display: 'flex',
        justifyContent: 'center',
        visibility: 'hidden',
        zIndex: 1
    },
    dragHandle: {
        width: '2px',
        height: '100%',
        backgroundColor: theme.palette.action.active,
        opacity: 0.5,
        transition: 'opacity 0.2s ease',
        '&:hover': {
            opacity: 0.8
        }
    },
    chatMessages: {
        flex: 1,
        padding: '16px',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        wordWrap: 'break-word',
    },
    message: {
        padding: '8px 12px',
        borderRadius: '12px',
        maxWidth: '80%',
        wordWrap: 'break-word',
        backdropFilter: 'blur(5px)',
    },
    ownMessage: {
        backgroundColor: 'rgba(25, 118, 210, 0.8)',
        color: 'white',
        alignSelf: 'flex-end',
        flexDirection: 'column',
        alignItems: 'flex-end',
    },
    otherMessage: {
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        color: 'white',
        alignSelf: 'flex-start',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    messageAuthor: {
        fontSize: '11px',
        fontWeight: 'normal',
        marginTop: '4px',
        opacity: 0.7,
        alignSelf: 'flex-start',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
    },
    messageTime: {
        fontSize: '10px',
        opacity: 0.6,
        marginLeft: '8px',
    },
    chatInput: {
        padding: '12px 16px',
        borderTop: '1px solid rgba(255, 255, 255, 0.2)',
        display: 'flex',
        gap: '8px',
        alignItems: 'flex-end',
        minWidth: 0,
        overflow: 'hidden',
    },
    inputField: {
        flex: 1,
        padding: '8px 12px',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        borderRadius: '20px',
        outline: 'none',
        fontSize: '14px',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        '&::placeholder': {
            color: 'rgba(255, 255, 255, 0.6)',
        },
        '&:focus': {
            borderColor: 'rgba(25, 118, 210, 0.8)',
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
        },
    },
    sendButton: {
        padding: '8px 12px',
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        border: 'none',
        borderRadius: '16px',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: 'bold',
        minWidth: '70px',
        height: '36px',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
        '&:disabled': {
            backgroundColor: theme.palette.grey[300],
            color: theme.palette.text.disabled,
            cursor: 'not-allowed',
        },
    },
    attachmentButton: {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        border: '2px solid rgba(255, 255, 255, 0.3)',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '16px',
        transition: 'all 0.3s ease',
        flexShrink: 0,
        '&:hover:not(:disabled)': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            borderColor: 'rgba(255, 255, 255, 0.5)',
            transform: 'scale(1.05)',
        },
        '&:active:not(:disabled)': {
            transform: 'scale(0.95)',
        },
        '&:disabled': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            color: 'rgba(255, 255, 255, 0.3)',
            cursor: 'not-allowed',
        },
        '&.uploading': {
            backgroundColor: 'rgba(33, 150, 243, 0.3)',
            borderColor: 'rgba(33, 150, 243, 0.5)',
            animation: 'pulse 1.5s infinite',
        },
    },
    toggleButton: {
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        width: '60px',
        height: '60px',
        borderRadius: '50%',
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        border: 'none',
        cursor: 'pointer',
        fontSize: '24px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        zIndex: 999,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    unreadBadge: {
        position: 'absolute',
        top: '-5px',
        right: '-5px',
        backgroundColor: theme.palette.error.main,
        color: 'white',
        borderRadius: '50%',
        width: '20px',
        height: '20px',
        fontSize: '12px',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    dateSeparator: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '16px 0',
        '&::before, &::after': {
            content: '""',
            flex: 1,
            height: '1px',
            backgroundColor: theme.palette.divider,
        },
        '&::before': {
            marginRight: '12px',
        },
        '&::after': {
            marginLeft: '12px',
        },
    },
    dateSeparatorText: {
        backgroundColor: theme.palette.background.paper,
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 'bold',
        color: theme.palette.text.secondary,
        border: `1px solid ${theme.palette.divider}`,
    },
    messageHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '4px',
    },
    copyButton: {
        marginTop: '2px',
        marginLeft: '2px',
        width: '20px',
        height: '20px',
        borderRadius: '4px',
        border: 'none',
        background: 'transparent',
        color: 'rgba(255,255,255,0.65)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0,
        transition: 'all 0.2s ease',
        '&:hover': {
            background: 'rgba(255,255,255,0.12)',
            color: '#fff',
        },
    },
    messageWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        '&:hover .copy-message-button': {
            opacity: 1,
        },
    },
    ownMessageWrapper: {
        alignItems: 'flex-end',
    },
    messageContent: {
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        whiteSpace: 'pre-wrap',
        maxWidth: '100%',
        lineHeight: 1.4,
    },
    scrollableMessageText: {
        '& p': {
            margin: '0 0 6px 0',
        },
        '& p:last-child': {
            marginBottom: 0,
        },
    },
    scrollableCodeMessage: {
        maxHeight: '160px',
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingRight: '6px',
        '& p': {
            margin: '0 0 6px 0',
        },
        '& p:last-child': {
            marginBottom: 0,
        },
        '& pre': {
            maxHeight: '140px',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            margin: 0,
        },
    },
    imageOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
    },
    overlayImage: {
        maxWidth: '95vw',
        maxHeight: '95vh',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
    },
    overlayCloseButton: {
        position: 'absolute',
        top: '20px',
        right: '20px',
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        color: 'white',
        border: 'none',
        borderRadius: '50%',
        width: '40px',
        height: '40px',
        fontSize: '20px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
        },
    },
    '@keyframes spin': {
        '0%': { transform: 'rotate(0deg)' },
        '100%': { transform: 'rotate(360deg)' }
    },
}));
const decodeHtml = (html) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = html;
    return textarea.value;
};
const highlightCodeInMessage = async (htmlContent) => {
    const highlighter = await initShiki();
    const theme = 'dark-plus';
    return htmlContent.replace(/<pre[^>]*>\s*<code([^>]*)>([\s\S]*?)<\/code>\s*<\/pre>/g, (match, codeAttrs, code) => {
        try {
            const langMatch = codeAttrs.match(/language-([a-zA-Z0-9_-]+)/);
            const language = langMatch?.[1] || 'plaintext';
            const decodedCode = decodeHtml(code);
            const highlighted = highlighter.codeToHtml(decodedCode, {
                lang: language,
                theme,
            });
            // Forcer les couleurs de syntaxe en ajoutant des styles inline
            const styledHighlighted = highlighted.replace(/<pre[^>]*>/, '<pre style="background: #2d2d2d !important; color: #f8f8f2 !important; margin: 0 !important; padding: 12px !important; border-radius: 8px !important; overflow: auto !important; font-family: monospace !important; font-size: 12px !important; line-height: 1.4 !important;">');
            return `<div class="chat-code-block" style="background: #2d2d2d !important; border-radius: 8px !important; margin: 8px 0 !important;">${styledHighlighted}</div>`;
        }
        catch (error) {
            return match;
        }
    });
};
const ChatAttachmentPreview = ({ attachment, messageId, roomId, onAttachmentUpdate }) => {
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [currentUrl, setCurrentUrl] = useState(attachment.readUrl || attachment.url);
    const [isUrlExpired, setIsUrlExpired] = useState(() => isExpired(attachment.readUrl || attachment.url));
    const [hasPreview, setHasPreview] = useState(true);
    useEffect(() => {
        const checkExpiration = () => {
            setIsUrlExpired(isExpired(currentUrl));
        };
        const interval = setInterval(checkExpiration, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [currentUrl]);
    useEffect(() => {
        const newUrl = attachment.readUrl || attachment.url;
        setCurrentUrl(newUrl);
        setIsUrlExpired(isExpired(newUrl));
        setHasPreview(true);
    }, [attachment.url, attachment.readUrl]);
    const refreshReadUrl = async () => {
        if (isRefreshing) {
            return null;
        }
        let gcpPath = attachment.gcpPath;
        if (!gcpPath && attachment.url) {
            gcpPath = buildGcpPathFromUrl(attachment.url);
        }
        if (!gcpPath) {
            return null;
        }
        setIsRefreshing(true);
        try {
            const response = await symfonyApi.post('/api/chat/refresh-read-url', {
                gcpPath
            });
            const readUrl = response.readUrl || response.data?.readUrl;
            const readUrlExpiresAt = response.readUrlExpiresAt || response.data?.readUrlExpiresAt;
            if (!readUrl) {
                return null;
            }
            const wsUrl = process.env.VITE_WS_URL;
            const nodeUrl = `${wsUrl}/api/chat/refresh-attachment`;
            const attachmentId = attachment.id || getAttachmentIdFromUrl(attachment.url);
            const nodeResponse = await fetch(nodeUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    roomId,
                    messageId,
                    attachmentId,
                    gcpPath,
                    readUrl,
                    readUrlExpiresAt
                })
            });
            const updatedAttachment = {
                ...attachment,
                url: readUrl,
                readUrl,
                readUrlExpiresAt,
                gcpPath
            };
            setCurrentUrl(readUrl);
            setIsUrlExpired(false);
            onAttachmentUpdate(messageId, updatedAttachment);
            return readUrl;
        }
        catch (error) {
            return null;
        }
        finally {
            setIsRefreshing(false);
        }
    };
    const handleReveal = async () => {
        if (isUrlExpired) {
            await refreshReadUrl();
            return;
        }
        showImageOnJitsiOverlay(currentUrl, attachment.name);
    };
    const renderRevealIcon = () => (React.createElement("div", { style: {
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.72)',
            border: '1px solid rgba(255, 255, 255, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.28)'
        } }, isRefreshing ? (React.createElement("span", { style: {
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            border: '2px solid rgba(255, 255, 255, 0.6)',
            borderTopColor: '#fff',
            animation: 'spin 0.8s linear infinite'
        } })) : (React.createElement("svg", { viewBox: "0 0 24 24", width: "22", height: "22", "aria-hidden": "true" },
        React.createElement("path", { fill: "rgba(17, 24, 39, 0.82)", d: "M12 5c5.2 0 8.4 4.1 9.5 5.8.5.7.5 1.7 0 2.4C20.4 14.9 17.2 19 12 19s-8.4-4.1-9.5-5.8a2.2 2.2 0 0 1 0-2.4C3.6 9.1 6.8 5 12 5Zm0 2C7.6 7 4.9 10.5 4.2 11.9a.3.3 0 0 0 0 .2C4.9 13.5 7.6 17 12 17s7.1-3.5 7.8-4.9a.3.3 0 0 0 0-.2C19.1 10.5 16.4 7 12 7Zm0 2.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5Z" })))));
    if (attachment.type?.startsWith('image/')) {
        if (isUrlExpired) {
            return (React.createElement("div", null,
                React.createElement("button", { type: "button", onClick: handleReveal, disabled: isRefreshing, "aria-label": "Rafra\u00EEchir et afficher l'image", style: {
                        width: '200px',
                        height: '150px',
                        position: 'relative',
                        display: 'block',
                        padding: 0,
                        border: 'none',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        cursor: isRefreshing ? 'wait' : 'pointer',
                        background: 'linear-gradient(135deg, #3f444d 0%, #232833 100%)'
                    } },
                    hasPreview && (React.createElement("img", { src: currentUrl, alt: "", "aria-hidden": "true", style: {
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            filter: 'blur(24px)',
                            transform: 'scale(1.1)',
                            opacity: 0.72
                        }, onError: () => setHasPreview(false) })),
                    React.createElement("div", { style: {
                            position: 'absolute',
                            inset: 0,
                            background: 'rgba(0, 0, 0, 0.2)'
                        } }),
                    React.createElement("div", { style: {
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        } }, renderRevealIcon())),
                React.createElement("div", { style: { fontSize: '11px', opacity: 0.7, textAlign: 'center', marginTop: '6px' } },
                    "\uD83D\uDCCE ",
                    attachment.name,
                    " (",
                    (attachment.size / 1024).toFixed(1),
                    " KB)")));
        }
        return (React.createElement("div", null,
            React.createElement("img", { src: currentUrl, alt: attachment.name, style: {
                    display: 'block',
                    maxWidth: '200px',
                    maxHeight: '200px',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    cursor: 'pointer'
                }, onClick: handleReveal, onError: () => setIsUrlExpired(true) }),
            React.createElement("div", { style: { fontSize: '11px', opacity: 0.7, textAlign: 'center' } },
                "\uD83D\uDCCE ",
                attachment.name,
                " (",
                (attachment.size / 1024).toFixed(1),
                " KB)")));
    }
    const handleAttachmentClick = async (event) => {
        if (!isUrlExpired) {
            return;
        }
        event.preventDefault();
        await refreshReadUrl();
    };
    if (isUrlExpired) {
        return (React.createElement("button", { type: "button", onClick: () => refreshReadUrl(), disabled: isRefreshing, style: {
                position: 'relative',
                border: '1px solid rgba(255, 255, 255, 0.16)',
                borderRadius: '6px',
                overflow: 'hidden',
                cursor: isRefreshing ? 'wait' : 'pointer',
                background: 'linear-gradient(135deg, #4b5563 0%, #1f2937 100%)',
                color: 'white',
                padding: '6px 8px',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                maxWidth: '200px'
            } },
            React.createElement("div", { style: {
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(0, 0, 0, 0.2)'
                } }),
            React.createElement("div", { style: {
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    minWidth: 0
                } },
                React.createElement("div", { style: {
                        transform: 'scale(0.64)',
                        transformOrigin: 'center',
                        width: '28px',
                        height: '28px',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    } }, renderRevealIcon()),
                React.createElement("div", { style: { minWidth: 0 } },
                    React.createElement("div", { style: {
                            fontSize: '12px',
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        } }, attachment.name),
                    React.createElement("div", { style: { fontSize: '11px', opacity: 0.72, marginTop: '4px' } },
                        (attachment.size / 1024).toFixed(1),
                        " KB")))));
    }
    return (React.createElement("a", { href: currentUrl, target: "_blank", rel: "noopener noreferrer", onClick: handleAttachmentClick, style: {
            color: '#4FC3F7',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            opacity: isUrlExpired ? 0.65 : 1,
            cursor: isRefreshing ? 'wait' : 'pointer'
        } },
        "\uD83D\uDCCE ",
        attachment.name,
        React.createElement("span", { style: { fontSize: '11px', opacity: 0.7 } },
            "(",
            (attachment.size / 1024).toFixed(1),
            " KB)"),
        isUrlExpired && (React.createElement("span", { style: { color: '#ff9800', fontSize: '11px' } }, isRefreshing ? 'Chargement...' : 'Cliquer pour rafraîchir'))));
};
export const OverlayChat = ({ isOpen, onToggle, userName, roomId }) => {
    const [userNamesCache, setUserNamesCache] = useState({});
    const [chatWidth, setChatWidth] = useState(350);
    const [isResizing, setIsResizing] = useState(false);
    const { classes } = useStyles({ width: chatWidth });
    const [copiedMessageId, setCopiedMessageId] = useState(null);
    const [highlightedMessages, setHighlightedMessages] = useState({});
    const [inputValue, setInputValue] = useState('');
    const [isConnected, setIsConnected] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [messages, setMessages] = useState([]);
    const [overlayImage, setOverlayImage] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const socketRef = useRef(null);
    const secureSocketRef = useRef(null);
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const loadingRef = useRef(false);
    const isLoadingMoreRef = useRef(false);
    const fileInputRef = useRef(null);
    const dispatch = useDispatch();
    const handleAttachmentUpdate = useCallback((messageId, updatedAttachment) => {
        setMessages(prev => prev.map(msg => msg.id === messageId
            ? { ...msg, attachment: updatedAttachment }
            : msg));
    }, []);
    const getRealSessionId = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const urlSessionId = urlParams.get('sessionId') || urlParams.get('session') || urlParams.get('t');
        const pathnameSession = window.location.pathname.split('/').pop();
        const realSessionId = urlSessionId || pathnameSession;
        if (!realSessionId) {
            const timestamp = urlParams.get('t') || Date.now().toString();
            return `session-${timestamp.slice(-8)}`;
        }
        return realSessionId;
    };
    const playNotificationSound = () => {
        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(392.00, audioContext.currentTime); // Sol (G4)
            gainNode.gain.setValueAtTime(0, audioContext.currentTime);
            gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01);
            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime + 0.08);
            gainNode.gain.linearRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
            // Deuxième note: Do (C5) - plus haute et plus courte
            oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime + 0.12);
            gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.13);
            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime + 0.18);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.25);
            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.25);
        }
        catch (error) {
            try {
                const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBi2Gy/DaizsIGGS57OihVhELTKXh8bllHgU7k9n1unEiBC13yO/eizEIHWq+6+OZURE');
                audio.volume = 0.5;
                audio.play();
            }
            catch (fallbackError) {
            }
        }
    };
    const getUserName = (msg) => {
        if (msg.senderFirstName && msg.senderLastName) {
            return formatName(msg.senderFirstName, msg.senderLastName);
        }
        if (msg.senderName)
            return msg.senderName;
        if (msg.username)
            return msg.username;
        if (msg.author) {
            const nameParts = msg.author.split(' ');
            if (nameParts.length >= 2) {
                return formatName(nameParts[0], nameParts.slice(1).join(' '));
            }
            return msg.author;
        }
        if (msg.user?.name)
            return msg.user.name;
        const senderId = Number(msg.senderId || msg.user?.id);
        if (senderId && userNamesCache[senderId]) {
            return userNamesCache[senderId];
        }
        return senderId ? `User ${senderId}` : 'Utilisateur';
    };
    const updateUserNamesCache = (msg) => {
        if ((msg.senderFirstName || msg.senderName || msg.username) && (msg.senderId || msg.user?.id)) {
            const senderId = Number(msg.senderId || msg.user?.id);
            const userName = getUserName(msg);
            if (senderId && userName !== `User ${senderId}`) {
                setUserNamesCache(prev => ({
                    ...prev,
                    [senderId]: userName
                }));
            }
        }
    };
    const handleIncomingMessage = (msg) => {
        const newMessage = mapServerMessageToClient(msg, getUserIdFromToken, updateUserNamesCache, getUserName);
        setMessages(prev => [...prev, newMessage]);
        if (!newMessage.isOwn) {
            playNotificationSound();
            updateUnread(prev => prev + 1);
        }
    };
    const updateUnread = (count) => {
        const newCount = typeof count === 'function' ? count(unreadCount) : count;
        setUnreadCount(newCount);
        localStorage.setItem('overlayChatUnreadCount', newCount.toString());
        window.dispatchEvent(new CustomEvent('overlayChatUnreadCount', { detail: newCount }));
    };
    const MAX_RECONNECT_ATTEMPTS = 5;
    const RECONNECT_BASE_DELAY = 2000;
    const reconnectAttemptsRef = useRef(0);
    const reconnectTimeoutRef = useRef(null);
    const scheduleReconnect = useCallback(() => {
        if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
            return;
        }
        if (reconnectTimeoutRef.current)
            return;
        const delay = RECONNECT_BASE_DELAY * Math.pow(2, reconnectAttemptsRef.current);
        reconnectAttemptsRef.current++;
        reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            if (isOpen && !socketRef.current) {
                connectSocket();
            }
        }, delay);
    }, [isOpen]);
    const clearReconnect = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
        reconnectAttemptsRef.current = 0;
    }, []);
    const connectSecureSocket = useCallback(() => {
        if (secureSocketRef.current?.connected) {
            return;
        }
        const token = getJwtToken();
        if (!token) {
            return;
        }
        try {
            const wsUrl = new URL(process.env.VITE_WS_URL);
            console.log('[OverlayChat] connectSecureSocket — VITE_WS_URL:', process.env.VITE_WS_URL);
            console.log('[OverlayChat] connectSecureSocket — origin:', wsUrl.origin, '| path:', `${wsUrl.pathname}/socket.io/`);
            const socket = io(`${wsUrl.origin}/secure`, {
                path: `${wsUrl.pathname}/socket.io/`,
                auth: { token },
                transports: ['websocket', 'polling'],
                forceNew: true,
                reconnection: true,
                reconnectionAttempts: 5,
                reconnectionDelay: 1000,
                withCredentials: true,
            });
            secureSocketRef.current = socket;
            socket.on(SocketEvent.CONNECT_ERROR, (_error) => {
            });
            socket.on(SocketEvent.FILE_MESSAGE, handleIncomingMessage);
        }
        catch (error) {
        }
    }, []);
    const connectSocket = useCallback(() => {
        if (socketRef.current?.connected) {
            return;
        }
        const userId = getUserIdFromToken();
        try {
            const wsUrl = new URL(process.env.VITE_WS_URL);
            const socket = io(`${wsUrl.origin}/public`, {
                path: `${wsUrl.pathname}/socket.io/`,
                transports: ['websocket', 'polling'],
                forceNew: true,
                reconnection: true,
                reconnectionAttempts: 5,
                reconnectionDelay: 1000,
                withCredentials: false,
            });
            socketRef.current = socket;
            setTimeout(() => {
                if (!socket.connected) {
                }
            }, 5000);
            socket.on(SocketEvent.CONNECT, () => {
                clearReconnect();
                setIsConnected(true);
                socket.emit(SocketEvent.JOIN, { roomId });
            });
            socket.on(SocketEvent.DISCONNECT, (reason) => {
                setIsConnected(false);
                scheduleReconnect();
            });
            socket.on(SocketEvent.CONNECT_ERROR, (_error) => {
                setIsConnected(false);
                scheduleReconnect();
            });
            socket.on(SocketEvent.HISTORY, (data) => {
                const container = messagesContainerRef.current;
                const mappedMessages = data.messages.map((msg) => mapServerMessageToClient(msg, getUserIdFromToken, updateUserNamesCache, getUserName));
                if (isLoadingMoreRef.current) {
                    const prevScrollHeight = container?.scrollHeight || 0;
                    setMessages(prev => [...mappedMessages, ...prev]);
                    setHasMore(data.hasMore !== undefined ? data.hasMore : false);
                    setTimeout(() => {
                        if (container) {
                            const newScrollHeight = container.scrollHeight;
                            container.scrollTop = newScrollHeight - prevScrollHeight;
                        }
                    }, 0);
                }
                else {
                    setMessages(mappedMessages);
                    setHasMore(data.hasMore !== undefined ? data.hasMore : false);
                    if (data.unreadCount !== undefined) {
                        updateUnread(data.unreadCount);
                    }
                    setTimeout(() => {
                        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                    }, 200);
                }
                loadingRef.current = false;
                isLoadingMoreRef.current = false;
                setLoadingMore(false);
            });
            socket.on(SocketEvent.NEW_MESSAGE, handleIncomingMessage);
            socket.on(SocketEvent.FILE_MESSAGE, handleIncomingMessage);
            socket.on(SocketEvent.MORE_MESSAGES, (data) => {
                const container = messagesContainerRef.current;
                const mappedMessages = data.messages.map((msg) => mapServerMessageToClient(msg, getUserIdFromToken, updateUserNamesCache, getUserName));
                const prevScrollHeight = container?.scrollHeight || 0;
                setMessages(prev => [...mappedMessages, ...prev]);
                setHasMore(data.hasMore !== undefined ? data.hasMore : false);
                setTimeout(() => {
                    if (container) {
                        const newScrollHeight = container.scrollHeight;
                        container.scrollTop = newScrollHeight - prevScrollHeight;
                    }
                }, 100);
                loadingRef.current = false;
                isLoadingMoreRef.current = false;
                setLoadingMore(false);
            });
        }
        catch (error) {
            setIsConnected(false);
            scheduleReconnect();
        }
    }, [roomId, scheduleReconnect, clearReconnect]);
    useEffect(() => {
        if (!socketRef.current) {
            connectSocket();
        }
        if (!secureSocketRef.current) {
            connectSecureSocket();
        }
        return () => {
        };
    }, [connectSocket, connectSecureSocket]);
    useEffect(() => {
        return () => {
            if (socketRef.current && socketRef.current.connected) {
                socketRef.current.emit(SocketEvent.LEAVE, { roomId });
            }
            clearReconnect();
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
            }
        };
    }, [clearReconnect, roomId]);
    useEffect(() => {
        if (isOpen) {
            dispatch(setFocusedTab(ChatTabs.CHAT));
            if (socketRef.current && socketRef.current.connected) {
                socketRef.current.emit(SocketEvent.MARK_AS_READ, { roomId });
            }
        }
    }, [isOpen, dispatch, roomId]);
    useEffect(() => {
        const lastMessage = messages[messages.length - 1];
        const hasAttachmentInLastMessage = lastMessage?.attachment?.type?.startsWith('image/');
        if (!isLoadingMoreRef.current && !hasAttachmentInLastMessage && messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);
    // Colorer le code dans les messages
    useEffect(() => {
        const highlightMessages = async () => {
            const newHighlightedMessages = {};
            for (const message of messages) {
                if (message.text && message.text.includes('<pre') && !highlightedMessages[message.id]) {
                    try {
                        const highlighted = await highlightCodeInMessage(message.text);
                        newHighlightedMessages[message.id] = highlighted;
                    }
                    catch (error) {
                        newHighlightedMessages[message.id] = message.text;
                    }
                }
            }
            if (Object.keys(newHighlightedMessages).length > 0) {
                setHighlightedMessages(prev => ({ ...prev, ...newHighlightedMessages }));
            }
        };
        highlightMessages();
    }, [messages]);
    useEffect(() => {
        if (isOpen && messagesEndRef.current) {
            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
    }, [isOpen]);
    useEffect(() => {
        chatToggleFunction = onToggle;
        return () => {
            chatToggleFunction = null;
        };
    }, [onToggle]);
    const formatTime = (timestamp) => {
        return new Date(timestamp).toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    const copyMessage = async (message) => {
        const plainText = message.text
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<\/p>/gi, '\n')
            .replace(/<[^>]*>/g, '')
            .trim();
        try {
            await navigator.clipboard.writeText(plainText);
            setCopiedMessageId(message.id);
            setTimeout(() => {
                setCopiedMessageId(null);
            }, 1200);
        }
        catch (err) {
        }
    };
    const formatDate = (timestamp) => {
        const date = new Date(timestamp);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        if (date.toDateString() === today.toDateString()) {
            return "Aujourd'hui";
        }
        if (date.toDateString() === yesterday.toDateString()) {
            return "Hier";
        }
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };
    const loadMoreMessages = useCallback(() => {
        if (!socketRef.current?.connected ||
            loadingRef.current ||
            !hasMore ||
            messages.length === 0) {
            return;
        }
        loadingRef.current = true;
        isLoadingMoreRef.current = true;
        setLoadingMore(true);
        const oldestMessage = messages[0];
        socketRef.current.emit(SocketEvent.LOAD_MORE, {
            roomId,
            before: oldestMessage.timestamp
        });
    }, [roomId, hasMore, messages]);
    const handleScroll = useCallback((e) => {
        const element = e.currentTarget;
        if (element.scrollTop <= 5 && hasMore && !loadingRef.current) {
            loadMoreMessages();
        }
    }, [loadMoreMessages, hasMore]);
    const MAX_MESSAGE_LENGTH = 50000;
    const sendMessage = (content) => {
        const messageContent = content?.trim() || '';
        if (!messageContent)
            return;
        if (messageContent.length > MAX_MESSAGE_LENGTH) {
            alert('Votre message est trop long. Réduisez-le ou envoyez-le en fichier.');
            return;
        }
        if (!socketRef.current?.connected) {
            alert('Connexion perdue. Impossible d\'envoyer le message.');
            return;
        }
        const userId = getUserIdFromToken();
        socketRef.current.emit(SocketEvent.MESSAGE, {
            roomId,
            content: messageContent,
            senderId: userId,
            senderName: userName || 'Anonymous User',
            timestamp: Date.now()
        });
    };
    const handleRichTextSend = (content) => {
        sendMessage(content);
    };
    const handleFileUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file)
            return;
        if (!symfonyApi.isAuthenticated()) {
            alert('Vous devez être connecté pour envoyer des pièces jointes');
            return;
        }
        const ALLOWED_IMAGE_TYPES = Object.values(AllowedFileType).filter(type => type.startsWith('image/'));
        const MAX_IMAGE_SIZE = FileSizeLimit.MAX_IMAGE_SIZE;
        const MAX_FILE_SIZE = FileSizeLimit.MAX_FILE_SIZE;
        const isImage = ALLOWED_IMAGE_TYPES.includes(file.type);
        if (isImage && file.size > MAX_IMAGE_SIZE) {
            alert('La taille maximale pour les images est de 5 MB');
            return;
        }
        if (!isImage && file.size > MAX_FILE_SIZE) {
            alert('Le fichier dépasse la taille maximale de 25 MB');
            return;
        }
        setIsUploading(true);
        setUploadProgress(0);
        try {
            const realSessionId = getRealSessionId();
            const urlResponse = await symfonyApi.post('/api/chat/upload-url', {
                fileName: file.name,
                fileSize: file.size,
                fileType: file.type,
                chatContext: ChatContext.LIVE_SESSION,
                roomId: realSessionId,
            });
            const readUrl = urlResponse.readUrl;
            const gcpPath = urlResponse.gcpPath || buildGcpPathFromUrl(readUrl);
            if (!readUrl || !gcpPath) {
                throw new Error('URL de lecture ou chemin GCP manquant pour la pièce jointe');
            }
            setUploadProgress(25);
            await uploadFileToGcp(file, urlResponse.uploadUrl, (progress) => {
                setUploadProgress(25 + Math.round(progress * 0.5));
            });
            setUploadProgress(100);
            if (socketRef.current) {
                if (!socketRef.current.connected) {
                    socketRef.current.connect();
                }
                setTimeout(() => {
                    if (secureSocketRef.current?.connected) {
                        const fileMetadata = {
                            roomId: roomId,
                            attachment: {
                                id: Date.now().toString(),
                                name: file.name,
                                url: readUrl,
                                readUrl,
                                size: file.size,
                                type: file.type,
                                uploadedAt: Date.now(),
                                gcpPath,
                                readUrlExpiresAt: urlResponse.readUrlExpiresAt
                            }
                        };
                        secureSocketRef.current.emit(SocketEvent.FILE_MESSAGE, fileMetadata);
                    }
                    else {
                        alert('Vous devez être connecté pour envoyer des fichiers');
                    }
                }, 500);
            }
        }
        catch (error) {
            alert('Erreur lors de l\'envoi du fichier: ' + (error instanceof Error ? error.message : 'Erreur inconnue'));
        }
        finally {
            setIsUploading(false);
            setUploadProgress(0);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };
    const uploadFileToGcp = (file, uploadUrl, onProgress) => {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable && onProgress) {
                    const progress = Math.round((event.loaded * 100) / event.total);
                    onProgress(progress);
                }
            });
            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve();
                }
                else {
                    reject(new Error(`Upload failed with status ${xhr.status}`));
                }
            });
            xhr.addEventListener('error', () => reject(new Error('Upload network error')));
            xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));
            xhr.open('PUT', uploadUrl);
            xhr.setRequestHeader('Content-Type', file.type);
            xhr.setRequestHeader('Accept', '*/*');
            xhr.send(file);
        });
    };
    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizing(true);
        const startX = e.clientX;
        const startWidth = chatWidth;
        const handleMouseMove = (ev) => {
            const newWidth = startWidth + (ev.clientX - startX);
            const minWidth = 200;
            const maxWidth = window.innerWidth * 0.8;
            if (newWidth >= minWidth && newWidth <= maxWidth) {
                setChatWidth(newWidth);
            }
        };
        const handleMouseUp = () => {
            setIsResizing(false);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [chatWidth]);
    if (!isOpen) {
        return (React.createElement("div", { style: { display: 'none' } }));
    }
    return (React.createElement("div", { className: classes.chatContainer },
        React.createElement("div", { className: classes.dragHandleContainer, onMouseDown: handleMouseDown },
            React.createElement("div", { className: classes.dragHandle })),
        React.createElement("div", { className: classes.chatHeader },
            React.createElement("h3", { className: classes.chatTitle },
                "Conversation: ",
                roomId),
            React.createElement("button", { className: classes.closeButton, onClick: onToggle }, "\u00D7")),
        React.createElement("div", { ref: messagesContainerRef, className: classes.chatMessages, onScroll: handleScroll },
            loadingMore && (React.createElement("div", { style: {
                    textAlign: 'center',
                    padding: '10px',
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '12px'
                } }, "Chargement des messages pr\u00E9c\u00E9dents...")),
            messages.map((message, index) => {
                const showDateSeparator = index === 0 ||
                    new Date(message.timestamp).toDateString() !== new Date(messages[index - 1].timestamp).toDateString();
                return (React.createElement(React.Fragment, { key: message.id },
                    showDateSeparator && (React.createElement("div", { className: classes.dateSeparator },
                        React.createElement("span", { className: classes.dateSeparatorText }, formatDate(message.timestamp)))),
                    React.createElement("div", { className: `${classes.messageWrapper} ${message.isOwn ? classes.ownMessageWrapper : ''}` },
                        React.createElement("div", { className: `${classes.message} ${message.isOwn ? classes.ownMessage : classes.otherMessage}` },
                            React.createElement("div", { className: classes.messageHeader },
                                React.createElement("span", { className: classes.messageAuthor }, message.author),
                                message.text && (React.createElement("button", { type: "button", className: `${classes.copyButton} copy-message-button`, onClick: () => copyMessage(message), title: "Copier" }, copiedMessageId === message.id ? '✓' : (React.createElement("svg", { viewBox: "0 0 24 24", width: "15", height: "15" },
                                    React.createElement("path", { fill: "currentColor", d: "M16 1H4C2.9 1 2 1.9 2 3v12h2V3h12V1zm3 4H8C6.9 5 6 5.9 6 7v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" }))))),
                                React.createElement("span", { className: classes.messageTime }, formatTime(message.timestamp))),
                            React.createElement("div", { className: classes.messageContent },
                                message.text && (React.createElement("div", { className: `${(message.text.includes('<pre') || (highlightedMessages[message.id] && highlightedMessages[message.id].includes('shiki'))) ? classes.scrollableCodeMessage : classes.scrollableMessageText} rich-text-message`, dangerouslySetInnerHTML: {
                                        __html: highlightedMessages[message.id] || message.text
                                    } })),
                                message.attachment && (React.createElement("div", { style: {
                                        padding: '8px',
                                        background: 'rgba(0,0,0,0.2)',
                                        borderRadius: '8px',
                                        marginTop: message.text ? '8px' : '0'
                                    } },
                                    React.createElement(ChatAttachmentPreview, { attachment: message.attachment, messageId: message.id, roomId: roomId, onAttachmentUpdate: handleAttachmentUpdate }))))))));
            }),
            React.createElement("div", { ref: messagesEndRef })),
        React.createElement("div", { className: classes.chatInput },
            React.createElement("input", { ref: fileInputRef, type: "file", onChange: handleFileUpload, style: { display: 'none' }, accept: `${Object.values(AllowedFileType).join(',')}` }),
            React.createElement("div", { style: { flex: '1 1 0', minWidth: 0, maxWidth: '100%' } },
                React.createElement(TiptapEditor, { onSend: handleRichTextSend, placeholder: symfonyApi.isAuthenticated() ? "Écrivez votre message..." : "Écrivez votre message...", disabled: false, onFileUpload: () => fileInputRef.current?.click(), isUploading: isUploading, uploadProgress: uploadProgress })))));
};
